import React, { useCallback, useState } from 'react';
import classNames from 'clsx';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';

import { useAutomationRecommendationsContext } from './automation-recommendations-context';
import { AutomationRecommendationsModal } from './automation-recommendations-modal';
import { ContactSalesModal } from './contact-sales-modal';
import { ExplanationModal } from './explanation-modal';
import messages from './messages';
import style from './style.module.less';
import { SuccessModal } from './success-modal';
import FormattedCurrency from '../../components/formatted-currency';
import { PromotionalBanner } from '../../components/promotional-banner';
import { AUTOMATION_RECOMMENDATION_BANNER_CLICKED, AUTOMATION_RECOMMENDATION_BANNER_SHOWN } from '../../constants/analytics-events';
import { useEnableAutomationRecommendations } from '../../feature-flags/hooks/use-enable-automation-recommendations';
import { useModal } from '../../hooks/use-modal';
import { Segment } from '../../services/analytics-services/segment';
import { LogService } from '../../services/log-service';
import Global from '../../store/global-selectors';

/**
 * @param {object} props
 * @param {boolean} props.roundCorners
 * @returns {ReactNode}
 */
export const AutomationRecommendationsBannerContent = ({ roundCorners = false }) => {
  const currentUser = useSelector(Global.selectUserCurrentUserInfo);
  const showFromFeatureFlag = useEnableAutomationRecommendations();
  const { totalSavings, recommendations } = useAutomationRecommendationsContext();
  const [selectedRecommendationId, setSelectedRecommendationId] = useState(null);
  const { close, isOpen, open } = useModal();
  const { close: closeContactModal, isOpen: contactModalIsOpen, open: openContactModal } = useModal();
  const { close: closeExplanationModal, isOpen: explanationModalIsOpen, open: openExplanationModal } = useModal();
  const { close: closeSuccessModal, isOpen: successModalIsOpen, open: openSuccessModal } = useModal();

  const openExplanation = useCallback(
    (id) => {
      setSelectedRecommendationId(id);
      openExplanationModal();
    },
    [openExplanationModal],
  );

  const openExploreSavings = useCallback(() => {
    open();
    try {
      Segment.trackEvent(AUTOMATION_RECOMMENDATION_BANNER_CLICKED, {
        currentUser,
        currentPath: window.location.pathname,
      });
    } catch (e) {
      LogService.logError(new Error('Could not contact segment', { cause: e }));
    }
  }, [currentUser, open]);

  if (!showFromFeatureFlag || recommendations.length === 0) {
    return null;
  }

  try {
    Segment.trackEvent(AUTOMATION_RECOMMENDATION_BANNER_SHOWN);
  } catch (e) {
    LogService.logError(new Error('Could not contact segment', { cause: e }));
  }

  return (
    <>
      <PromotionalBanner
        className={classNames(style.banner, { [style.round]: roundCorners })}
        promotionalHeaderMessage={
          <FormattedMessage
            {...messages.bannerMainText}
            values={{
              savingsAmount: <FormattedCurrency currencyCode={totalSavings.currencyCode} value={totalSavings.value} />,
            }}
          />
        }
        sparkly
      >
        <Button onClick={openExploreSavings} theme='tertiary'>
          <FormattedMessage {...messages.exploreSavings} />
        </Button>
      </PromotionalBanner>
      {isOpen ? (
        <AutomationRecommendationsModal onClose={close} openContactModal={openContactModal} openExplanationModal={openExplanation} />
      ) : null}
      {contactModalIsOpen ? (
        <ContactSalesModal
          onClose={closeContactModal}
          onSuccess={openSuccessModal}
          recommendationId={selectedRecommendationId || recommendations[0].id}
        />
      ) : null}
      {explanationModalIsOpen ? (
        <ExplanationModal
          id={selectedRecommendationId}
          onClose={closeExplanationModal}
          openContactSalesModal={() => {
            openContactModal();
            closeExplanationModal();
          }}
        />
      ) : null}
      {successModalIsOpen ? <SuccessModal onClose={closeSuccessModal} /> : null}
    </>
  );
};
